import { useActiveProduct } from "@/core/context/ActiveProductContext"
import makeUseStyles from "@/core/ui/style/util/makeUseStyles"
import { DashboardBlockFormStore } from "@/dashboard/blocks/kinds/DashboardBlockKindForms"
import { DashboardBlockFeedView } from "@/dashboard/blocks/kinds/__generated__/FeedDashboardBlockFragment.graphql"
import { useDashboardContext } from "@/dashboard/context/DashboardContext"
import CarouselView from "@assets/images/covers/dashboard-blocks/carousel-view.svg"
import ScrollView from "@assets/images/covers/dashboard-blocks/scroll-view.svg"
import { DiscoCheckbox, DiscoFormControl, DiscoInput, DiscoTooltip } from "@disco-ui"
import DiscoRadioWithImage from "@disco-ui/radio/DiscoRadioWithImage"
import { RadioGroup } from "@material-ui/core"
import { observer } from "mobx-react-lite"

interface Props {
  form: DashboardBlockFormStore
}

function FeedDashboardBlockForm(props: Props) {
  const { form } = props
  const { feed, position } = form.state
  const classes = useStyles()
  const activeProduct = useActiveProduct()
  const { isOneColumn } = useDashboardContext()!
  const canEditView = !isOneColumn && position !== "side"

  if (!feed) return null

  return (
    <>
      <DiscoFormControl label={"Title"} errorMessages={form.errorsByField.title}>
        <DiscoInput
          value={feed.title}
          onChange={(e) => (feed.title = e.target.value)}
          fullWidth
        />
      </DiscoFormControl>

      {!activeProduct && (
        <DiscoFormControl>
          <DiscoFormControl label={"Select Format"}>
            <RadioGroup
              value={feed.view}
              onChange={(e) => (feed.view = e.target.value as DashboardBlockFeedView)}
              className={classes.radioGroup}
            >
              <DiscoTooltip
                content={
                  isOneColumn
                    ? "Only carousel format is supported for pages with 1-column layout."
                    : position === "side"
                    ? "The Posts block on side column is only supported in carousel format. To use scrollable format, move this block to the main middle column."
                    : null
                }
              >
                <div>
                  <DiscoRadioWithImage
                    testid={`FeedDashboardBlockForm.radio.card`}
                    className={classes.radioButton}
                    label={"Carousel"}
                    sublabel={<CarouselView />}
                    value={"card"}
                    checked={feed.view === "card"}
                    disabled={!canEditView}
                  />
                </div>
              </DiscoTooltip>
              <DiscoRadioWithImage
                testid={`FeedDashboardBlockForm.radio.list`}
                className={classes.radioButton}
                label={"Scrollable"}
                sublabel={<ScrollView />}
                value={"list"}
                checked={feed.view === "list"}
                disabled={!canEditView}
              />
            </RadioGroup>
          </DiscoFormControl>
        </DiscoFormControl>
      )}

      {feed.view === "card" && (
        <DiscoFormControl>
          <DiscoCheckbox
            label={`Show post's cover image`}
            checked={Boolean(feed.showCover)}
            onChange={() => (feed.showCover = !feed.showCover)}
          />
        </DiscoFormControl>
      )}
    </>
  )
}

const useStyles = makeUseStyles((theme) => ({
  radioGroup: {
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: theme.spacing(2),
    display: "grid",
    gridAutoFlow: "column",
    flex: "1 1 auto",

    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  radioButton: {
    flexGrow: 0,
    width: "100%",
    height: "100%",
  },
}))

export default observer(FeedDashboardBlockForm)
